import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Star, MapPin, Wifi, ParkingCircle, CreditCard, Baby, Award, ShieldCheck, Clock, Navigation, Instagram, Facebook, Heart, Play, BadgeCheck, X, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Reveal, LineReveal } from "@/components/motion";
import ReviewForm from "@/components/ReviewForm";
import { SOCIALS } from "@/lib/socials";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .59.05.87.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const IMG = {
  hero: "/hero-reek.jpg",
  shop: "/work/shop.jpeg",
  work: ["/work/shop.jpeg", "/work/work1.jpeg", "/work/work2.jpeg", "/work/work3.jpeg", "/work/work4.jpeg", "/work/work5.jpeg", "/work/work6.jpeg"],
  texture: "https://images.pexels.com/photos/2248589/pexels-photo-2248589.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const AMENITIES = [
  { icon: ParkingCircle, label: "Parking Space" },
  { icon: CreditCard, label: "Credit Cards Accepted" },
  { icon: Baby, label: "Child-Friendly" },
  { icon: Wifi, label: "Wi-Fi" },
  { icon: Award, label: "Loyalty Program" },
];

const CHAPTERS = [
  { n: "01", title: "The Craft", body: "Every fade, lineup and shave is executed with surgical precision. This isn't a quick trim — it's a study in detail, sharpened over years behind the chair." },
  { n: "02", title: "The Experience", body: "Hot towels, straight-razor lines and honest conversation. You leave looking sharp and feeling like the best version of yourself." },
  { n: "03", title: "The Standard", body: "Disinfection between every client, a spotless station and consistency you can set your calendar to. Excellence, every single visit." },
];

function useServices() {
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);
  return services;
}

function NextAvailableBadge() {
  const [slot, setSlot] = useState(null);
  useEffect(() => {
    api.get("/next-available").then((r) => { if (r.data.available) setSlot(r.data); }).catch(() => {});
  }, []);
  if (!slot) return null;
  const to12 = (t) => {
    if (!t || typeof t !== "string" || !t.includes(":")) return "";
    const [h, m] = t.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return "";
    const ap = h >= 12 ? "PM" : "AM";
    const hh = h % 12 || 12;
    return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.8 }}
      className="mb-8"
    >
      <Link
        to={`/book?date=${slot.date}`}
        className="group inline-flex items-center gap-3 border border-[#d4af37]/40 bg-[#d4af37]/5 backdrop-blur-sm px-4 py-2.5 hover:bg-[#d4af37]/10 transition-colors duration-300"
        data-testid="next-available-badge"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#d4af37] opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#d4af37]" />
        </span>
        <Clock className="w-3.5 h-3.5 text-[#d4af37]" strokeWidth={2} />
        <span className="overline text-[0.65rem] text-white">
          Next opening: <span className="text-[#d4af37]">{slot.label} · {to12(slot.time)}</span>
          {slot.location_short && <span className="text-white/40"> · {slot.location_short}</span>}
        </span>
        <ArrowRight className="w-3.5 h-3.5 text-[#d4af37] transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
      </Link>
    </motion.div>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden" data-testid="hero">
      {/* Background portrait — biased toward face on wide viewports */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMG.hero}
          alt="Reek — The Barbers Chair, Clayton NC"
          className="w-full h-full object-cover object-[70%_28%] lg:object-[65%_30%]"
        />
        {/* Left-to-right dark wash keeps the copy on the left crisp */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/90 via-[#050505]/55 to-[#050505]/25 lg:to-transparent" />
        {/* Vertical fade to blend into the page */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/70" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 min-h-[100svh] max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-16 lg:pb-24 pt-32 lg:pt-40"
      >
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 1 }}
          className="overline text-[0.65rem] lg:text-xs text-[#d4af37] mb-6 tracking-[0.35em] flex items-center gap-3"
          data-testid="hero-tagline"
        >
          <span className="w-8 h-px bg-[#d4af37]" /> Precision. Confidence. Legacy.
        </motion.p>

        <LineReveal
          className="font-display text-white leading-[0.95] mb-8"
          lineClassName="text-5xl sm:text-6xl lg:text-7xl font-medium"
          lines={[
            <>Where every</>,
            <><span className="italic text-[#d4af37]">cut</span> tells</>,
            <>a story.</>,
          ]}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 1 }}
          className="text-white/70 text-base lg:text-lg max-w-md font-body leading-relaxed mb-6"
          data-testid="hero-subtext"
        >
          Precision cuts, sharp lineups and a grooming experience built on excellence.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 1 }}
          className="flex items-center gap-2 text-white/80 font-body text-sm mb-4"
          data-testid="hero-location"
        >
          <MapPin className="w-4 h-4 text-[#d4af37]" strokeWidth={1.75} />
          <span className="overline text-[0.65rem] tracking-[0.25em]">Wilson & Clayton, North Carolina</span>
        </motion.div>

        <div className="mb-8">
          <NextAvailableBadge />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 1 }}
          className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4"
        >
          <Link
            to="/book"
            className="group inline-flex items-center justify-center gap-3 bg-[#d4af37] text-black px-8 py-4 overline text-xs tracking-[0.2em] hover:bg-[#c5a028] transition-colors duration-300"
            data-testid="hero-book-btn"
          >
            Book an Appointment
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
          </Link>
          <a
            href="#memberships"
            className="inline-flex items-center justify-center gap-3 border border-[#d4af37] text-[#d4af37] px-8 py-4 overline text-xs tracking-[0.2em] hover:bg-[#d4af37] hover:text-black transition-colors duration-300"
            data-testid="hero-memberships-btn"
          >
            View Memberships
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 1 }}
          className="hidden lg:flex items-center gap-6 mt-10"
        >
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 fill-[#d4af37] text-[#d4af37]" />
            <span className="text-white font-body text-sm"><b>4.9</b> <span className="text-white/50">· 12 reviews</span></span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

function MarqueeStrip() {
  return (
    <section className="py-10 border-y border-white/10 bg-[#0a0a0a]" data-testid="marquee">
      <Marquee speed={40} gradient={false}>
        {["Fades", "Lineups", "Beard Work", "Head Shaves", "Signature Cuts", "Kids Cuts"].map((t, i) => (
          <span key={i} className="font-display italic text-5xl lg:text-7xl text-stroke-gold mx-10 select-none">
            {t} <span className="text-[#d4af37] not-italic">✦</span>
          </span>
        ))}
      </Marquee>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="py-32 px-6 lg:px-12 max-w-[1400px] mx-auto" data-testid="manifesto">
      <Reveal>
        <p className="overline text-xs text-[#d4af37] mb-4">Why the chair</p>
        <h2 className="font-display text-4xl lg:text-6xl text-white max-w-2xl leading-tight">
          More than a haircut. A <span className="italic text-[#d4af37]">standard</span>.
        </h2>
      </Reveal>

      <div className="mt-20 grid md:grid-cols-3 gap-x-12 gap-y-16">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.1}>
            <div className="border-t border-[#d4af37]/30 pt-8">
              <span className="font-display text-7xl lg:text-8xl text-white/10 block leading-none mb-6">{c.n}</span>
              <h3 className="font-label uppercase tracking-[0.15em] text-lg text-white mb-4">{c.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed font-body">{c.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function MeetReek() {
  const specialties = ["Fades", "Tapers", "Beard Grooming", "Premium Experiences"];
  return (
    <section id="about" className="py-32 px-6 lg:px-12 max-w-[1400px] mx-auto" data-testid="meet-reek-section">
      <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        <Reveal>
          <div className="relative w-full max-w-md mx-auto lg:mx-0">
            <div className="absolute -inset-3 border border-[#d4af37]/30" />
            <div className="relative overflow-hidden">
              <img
                src="/work/reek.webp"
                alt="Reek — Master Barber at The Barbers Chair"
                className="w-full h-[560px] object-cover object-top"
                data-testid="reek-photo"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>
            <div className="absolute -bottom-5 -right-5 bg-[#d4af37] text-black px-6 py-4 shadow-xl">
              <p className="font-display text-4xl leading-none">5+</p>
              <p className="overline text-[0.55rem] mt-1">Years Pro</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="overline text-xs text-[#d4af37] mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-[#d4af37]" /> The Barber
          </p>
          <h2 className="font-display text-5xl lg:text-7xl text-white mb-6 leading-none">
            Meet <span className="italic text-[#d4af37]">Reek</span>
          </h2>
          <p className="text-white/60 text-lg leading-relaxed font-body mb-8 max-w-lg">
            A 5-year professional barber specializing in fades, tapers, beard grooming, and premium grooming experiences. Every client leaves the chair sharper, cleaner, and more confident than they walked in.
          </p>

          <div className="flex flex-wrap gap-3 mb-10">
            {specialties.map((s) => (
              <span key={s} className="border border-white/15 px-4 py-2 overline text-[0.6rem] text-white/70">{s}</span>
            ))}
          </div>

          <div className="flex items-center gap-8 mb-10">
            <div>
              <p className="font-display text-4xl text-[#d4af37]">4.9</p>
              <p className="overline text-[0.55rem] text-white/40 mt-1">Client Rating</p>
            </div>
            <div className="w-px h-12 bg-white/10" />
            <div>
              <p className="font-display text-4xl text-[#d4af37]">5+</p>
              <p className="overline text-[0.55rem] text-white/40 mt-1">Years Experience</p>
            </div>
          </div>

          <Link
            to="/book"
            className="group inline-flex items-center gap-3 bg-[#d4af37] text-black px-10 py-4 overline text-xs hover:bg-[#c5a028] transition-colors duration-300"
            data-testid="meet-reek-book-btn"
          >
            Book with Reek
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Services({ services }) {
  const ordered = [...services].sort((a, b) => (b.featured - a.featured) || (b.price - a.price));
  return (
    <section id="services" className="py-32 px-6 lg:px-12 bg-[#0a0a0a] border-y border-white/10" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto">
        <Reveal>
          <div className="max-w-2xl mb-12">
            <p className="overline text-xs text-[#d4af37] mb-4 flex items-center gap-3"><span className="w-8 h-px bg-[#d4af37]" /> Services & Pricing</p>
            <h2 className="font-display text-4xl lg:text-6xl text-white leading-[1.05] mb-6">An investment in <span className="italic text-[#d4af37]">how you show up.</span></h2>
            <p className="text-white/60 font-body text-base leading-relaxed">
              Every appointment at The Barbers Chair is designed to deliver more than a haircut — it&apos;s a premium grooming experience focused on precision, professionalism and consistency. Book with confidence knowing you&apos;ll leave looking your best.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6">
          {ordered.map((s, i) => (
            <Reveal key={s.id} delay={Math.min(i * 0.06, 0.3)} y={20}>
              <div className={`relative flex flex-col justify-between border p-8 h-full transition-colors duration-300 ${s.featured ? "border-[#d4af37] bg-gradient-to-br from-[#d4af37]/[0.08] to-transparent" : "border-white/10 bg-[#050505] hover:border-[#d4af37]/40"}`} data-testid={`service-card-${s.id}`}>
                {s.featured && (
                  <span className="absolute -top-3 left-8 bg-[#d4af37] text-black overline text-[0.6rem] px-3 py-1 tracking-widest" data-testid={`most-popular-${s.id}`}>Most Popular</span>
                )}
                <div>
                  <h3 className="font-display text-2xl lg:text-3xl text-white mb-2">{s.name}</h3>
                  <p className="text-white/50 font-body text-sm leading-relaxed mb-6">{s.description}</p>
                </div>
                <div className="flex items-end justify-between gap-4">
                  <div>
                    {s.starting_at && <p className="overline text-[0.55rem] text-[#d4af37]/80 mb-1">Starting at</p>}
                    <p className="font-display text-4xl lg:text-5xl text-white leading-none">${s.price}</p>
                    <p className="overline text-[0.55rem] text-white/40 mt-2">{s.duration} minutes</p>
                  </div>
                  <Link
                    to={`/book?service=${s.id}`}
                    className={`group inline-flex items-center gap-2 px-6 py-3 overline text-[0.65rem] transition-colors duration-300 ${s.featured ? "bg-[#d4af37] text-black hover:bg-[#c5a028]" : "border border-white/20 text-white hover:border-[#d4af37] hover:text-[#d4af37]"}`}
                    data-testid={`service-book-${s.id}`}
                  >
                    Reserve Your Chair
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const INCLUDED = [
  { title: "Professional consultation", body: "We start with a quick conversation about your look, your lifestyle, and what will suit you best." },
  { title: "Precision haircut", body: "Sharp, clean lines, blended fades, and shape that grows out well between visits." },
  { title: "Razor lineup", body: "A crisp, straight-razor lineup to frame the cut and finish edges cleanly." },
  { title: "Hot towel finish", body: "A warm towel to open pores, remove product residue and reset your skin." },
  { title: "Style & finish", body: "Cut styled with the right product for your hair type — leave the chair ready for anywhere." },
  { title: "Sanitized tools", body: "Fresh guards, disinfected shears and razors — safety and hygiene, always." },
  { title: "Premium products", body: "Barber-grade pomades, oils and aftershave curated for a lasting hold and healthy skin." },
];

function WhatsIncluded() {
  return (
    <section id="whats-included" className="py-32 px-6 lg:px-12 max-w-[1400px] mx-auto" data-testid="whats-included-section">
      <Reveal>
        <p className="overline text-xs text-[#d4af37] mb-4 flex items-center gap-3"><span className="w-8 h-px bg-[#d4af37]" /> Every Cut Includes</p>
        <h2 className="font-display text-4xl lg:text-6xl text-white mb-4">The <span className="italic text-[#d4af37]">full ritual</span>.</h2>
        <p className="text-white/50 font-body mb-14 max-w-xl">The price you see is the full experience. No upsells at the chair — just excellent work, every time.</p>
      </Reveal>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
        {INCLUDED.map((it, i) => (
          <Reveal key={it.title} delay={(i % 3) * 0.05} y={16}>
            <div className="border-l-2 border-[#d4af37] pl-5" data-testid={`included-${i}`}>
              <p className="font-display text-lg text-white mb-2">{it.title}</p>
              <p className="text-white/50 font-body text-sm leading-relaxed">{it.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const MEMBERSHIPS = [
  { name: "The Signature", monthly: 79, per_visit: 50, included: 2,
    perks: ["2 Executive Haircut + Beard services / month", "Priority booking window", "10% off any add-on"], tag: null },
  { name: "The Chairman", monthly: 150, per_visit: 50, included: 4,
    perks: ["4 Executive Haircut + Beard services / month (weekly chair)", "Priority booking + after-hours access", "One complimentary Executive Facial every quarter", "10% off any add-on"], tag: "Best Value" },
];

function Memberships() {
  return (
    <section id="memberships" className="py-32 px-6 lg:px-12 bg-[#0a0a0a] border-y border-white/10" data-testid="memberships-section">
      <div className="max-w-[1400px] mx-auto">
        <Reveal>
          <div className="max-w-2xl mb-14">
            <p className="overline text-xs text-[#d4af37] mb-4 flex items-center gap-3"><span className="w-8 h-px bg-[#d4af37]" /> Memberships</p>
            <h2 className="font-display text-4xl lg:text-6xl text-white leading-[1.05] mb-4">Look sharp <span className="italic text-[#d4af37]">every week.</span></h2>
            <p className="text-white/60 font-body">For the client who takes their look seriously. Fewer decisions, better rates, guaranteed chair time.</p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {MEMBERSHIPS.map((m, i) => {
            const perVisitValue = m.per_visit * m.included;
            const savings = perVisitValue - m.monthly;
            const featured = m.tag === "Best Value";
            const smsBody = encodeURIComponent(`Hi Reek, I'd like to join ${m.name} membership.`);
            return (
              <Reveal key={m.name} delay={i * 0.08} y={20}>
                <div className={`relative flex flex-col border p-8 h-full ${featured ? "border-[#d4af37] bg-gradient-to-br from-[#d4af37]/[0.08] to-transparent" : "border-white/10 bg-[#050505]"}`} data-testid={`membership-${m.name.toLowerCase().replace(/[^a-z]/g,'-')}`}>
                  {m.tag && <span className="absolute -top-3 left-8 bg-[#d4af37] text-black overline text-[0.6rem] px-3 py-1 tracking-widest">{m.tag}</span>}
                  <p className="overline text-xs text-[#d4af37] mb-2">{m.name}</p>
                  <div className="flex items-end gap-2 mb-1">
                    <span className="font-display text-5xl text-white leading-none">${m.monthly}</span>
                    <span className="text-white/40 font-body text-sm pb-1">/ month</span>
                  </div>
                  <p className="text-white/40 font-body text-sm mb-6">
                    <span className="line-through">${perVisitValue}</span> a-la-carte · <span className="text-[#d4af37]">save ${savings}/mo</span>
                  </p>
                  <ul className="space-y-3 mb-8">
                    {m.perks.map((p) => (
                      <li key={p} className="flex gap-3 text-white/70 font-body text-sm">
                        <span className="text-[#d4af37] shrink-0">—</span> {p}
                      </li>
                    ))}
                  </ul>
                  <a href={`sms:+12523153416?&body=${smsBody}`} className={`mt-auto group inline-flex items-center justify-center gap-2 py-3.5 overline text-xs transition-colors duration-300 ${featured ? "bg-[#d4af37] text-black hover:bg-[#c5a028]" : "border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-black"}`} data-testid={`join-${m.name.toLowerCase().replace(/[^a-z]/g,'-')}`}>
                    Become a Member
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                  </a>
                </div>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-12 border border-white/10 bg-[#0a0a0a] p-6 max-w-3xl mx-auto" data-testid="member-faq">
          <p className="overline text-xs text-[#d4af37] mb-4 flex items-center gap-2">
            <ChevronRight className="w-3.5 h-3.5" /> How it works
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <p className="font-body text-white text-sm font-medium mb-1">1 · Enroll</p>
              <p className="text-white/50 font-body text-sm leading-relaxed">Tap &quot;Become a Member&quot; and text Reek at (252) 315-3416 to set up monthly billing.</p>
            </div>
            <div>
              <p className="font-body text-white text-sm font-medium mb-1">2 · Book normally</p>
              <p className="text-white/50 font-body text-sm leading-relaxed">Use the online booking as usual — members get a priority window every week.</p>
            </div>
            <div>
              <p className="font-body text-white text-sm font-medium mb-1">3 · Redeem at the chair</p>
              <p className="text-white/50 font-body text-sm leading-relaxed">Reek applies your included-visit credit in person. Add-ons bill with your 10% member discount.</p>
            </div>
          </div>
        </div>
        <p className="text-white/30 font-body text-xs text-center mt-6">Memberships bill monthly and can be cancelled anytime.</p>
      </div>
    </section>
  );
}

function Gallery() {
  const [uploaded, setUploaded] = useState([]);
  useEffect(() => {
    api.get("/gallery").then((r) => setUploaded(r.data)).catch(() => {});
  }, []);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const items = [
    ...uploaded.map((g) => ({ src: `${backend}${g.url}`, caption: g.caption })),
    ...IMG.work.map((src) => ({ src, caption: "" })),
  ];
  return (
    <section id="gallery" className="py-32 px-6 lg:px-12 max-w-[1400px] mx-auto" data-testid="gallery-section">
      <Reveal>
        <div className="flex items-end justify-between mb-14 gap-6">
          <div>
            <p className="overline text-xs text-[#d4af37] mb-4">The Work</p>
            <h2 className="font-display text-4xl lg:text-6xl text-white">Fresh off the chair</h2>
          </div>
          <p className="hidden md:block text-white/40 text-sm max-w-xs font-body">Real cuts. Real clients. Every line earned by Reek.</p>
        </div>
      </Reveal>
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [&>*]:mb-6">
        {items.map((item, i) => (
          <Reveal key={i} delay={(i % 3) * 0.08}>
            <div className="relative overflow-hidden border border-white/10 group break-inside-avoid" data-testid={`gallery-img-${i}`}>
              <img
                src={item.src}
                alt={item.caption || `Cut by Reek ${i + 1}`}
                loading="lazy"
                className="w-full h-auto object-cover scale-105 group-hover:scale-100 transition-transform duration-700 ease-out"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-[#d4af37]/0 group-hover:ring-[#d4af37]/40 transition-[box-shadow] duration-500" />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="overline text-[0.55rem] text-[#d4af37]">{item.caption || "The Barbers Chair"}</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Reviews({ services = [] }) {
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <section id="reviews" className="py-32 px-6 lg:px-12 bg-[#0a0a0a] border-y border-white/10" data-testid="reviews-section">
      <div className="max-w-[1400px] mx-auto">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <p className="overline text-xs text-[#d4af37] mb-4">Word on the street</p>
              <h2 className="font-display text-4xl lg:text-6xl text-white">Client Reviews</h2>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-display text-6xl text-[#d4af37]">4.9</span>
              <div>
                <div className="flex gap-1 mb-1">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#d4af37] text-[#d4af37]" />)}
                </div>
                <p className="text-white/50 text-sm font-body mb-3">Based on real client visits</p>
                <ReviewForm services={services} />
              </div>
            </div>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6">
          {reviews.map((r, i) => (
            <Reveal key={r.id} delay={Math.min(i * 0.05, 0.3)} y={24}>
              <div className="border border-white/10 p-8 h-full bg-[#050505] hover:border-[#d4af37]/40 transition-colors duration-500" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#d4af37] text-black flex items-center justify-center font-display text-lg">{r.author[0]}</div>
                    <div>
                      <p className="font-body text-white text-sm font-semibold">{r.author}</p>
                      <p className="overline text-[0.55rem] text-white/40">{r.date}</p>
                    </div>
                  </div>
                  <div className="flex gap-0.5">
                    {[...Array(r.rating)].map((_, j) => <Star key={j} className="w-3 h-3 fill-[#d4af37] text-[#d4af37]" />)}
                  </div>
                </div>
                <p className="overline text-[0.55rem] text-[#d4af37]/70 mb-3">{r.service}</p>
                <p className="text-white/70 text-sm leading-relaxed font-body">{r.text}</p>
                {r.response && (
                  <div className="mt-5 pl-4 border-l-2 border-[#d4af37]/40">
                    <p className="overline text-[0.5rem] text-white/40 mb-1">Reek responded</p>
                    <p className="text-white/50 text-xs italic leading-relaxed font-body">{r.response}</p>
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const LOCATIONS_STATIC = [
  {
    id: "wilson",
    name: "Wilson (Main Shop)",
    short: "Wilson",
    address: "4000 Ward Blvd, Unit E, Wilson, NC 27893",
    hours: "Tue–Sun · 9:00 AM – 7:00 PM",
    tag: "Main Location",
    query: "4000+Ward+Blvd+Unit+E,+Wilson,+NC+27893",
  },
  {
    id: "clayton",
    name: "Clayton (Nights & Off-Days)",
    short: "Clayton",
    address: "543 Averasboro Drive, Clayton, NC 27520",
    hours: "Nightly · 9:00 PM – 11:00 PM",
    tag: "By appointment",
    query: "543+Averasboro+Drive,+Clayton,+NC+27520",
  },
];

function Visit() {
  const [activeLoc, setActiveLoc] = useState(LOCATIONS_STATIC[0].id);
  const active = LOCATIONS_STATIC.find((l) => l.id === activeLoc) || LOCATIONS_STATIC[0];
  return (
    <section id="visit" className="py-32 px-6 lg:px-12 max-w-[1400px] mx-auto" data-testid="visit-section">
      <div className="grid lg:grid-cols-2 gap-16 items-start">
        <Reveal>
          {/* Location switcher */}
          <div className="flex gap-2 mb-4" data-testid="visit-location-tabs">
            {LOCATIONS_STATIC.map((l) => (
              <button
                key={l.id}
                onClick={() => setActiveLoc(l.id)}
                className={`overline text-[0.65rem] px-4 py-2 border transition-colors ${activeLoc === l.id ? "bg-[#d4af37] text-black border-[#d4af37]" : "border-white/15 text-white/60 hover:border-[#d4af37] hover:text-[#d4af37]"}`}
                data-testid={`visit-tab-${l.id}`}
              >
                {l.short}
              </button>
            ))}
          </div>

          <div className="relative overflow-hidden border border-white/10 h-[480px]" data-testid="visit-map">
            <iframe
              title={`The Barbers Chair — ${active.short}`}
              key={active.id}
              src={`https://maps.google.com/maps?q=${active.query}&z=15&output=embed`}
              className="w-full h-full grayscale-[0.4] contrast-[1.05]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="absolute top-0 left-0 right-0 p-6 pointer-events-none bg-gradient-to-b from-[#050505] to-transparent">
              <p className="overline text-xs text-[#d4af37] flex items-center gap-2">
                <MapPin className="w-4 h-4" strokeWidth={1.5} /> {active.tag}
              </p>
              <p className="font-display text-xl lg:text-2xl text-white leading-snug mt-2">{active.address}</p>
              <p className="overline text-[0.6rem] text-white/50 mt-2 tracking-widest">{active.hours}</p>
            </div>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${active.query}`}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-5 left-5 group inline-flex items-center gap-2 bg-[#d4af37] text-black px-6 py-3 overline text-[0.65rem] hover:bg-[#c5a028] transition-colors duration-300"
              data-testid="get-directions-btn"
            >
              <Navigation className="w-4 h-4" strokeWidth={2} /> Get Directions
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="overline text-xs text-[#d4af37] mb-4">Two locations</p>
          <h2 className="font-display text-4xl lg:text-5xl text-white mb-4">Wilson by day.<br /><span className="italic text-[#d4af37]">Clayton</span> by night.</h2>
          <p className="text-white/50 font-body mb-10 max-w-lg leading-relaxed">
            The main shop is on Ward Blvd in Wilson — walk-ins welcome, standard hours.
            Need a late cut? Reek runs private evening chairs in Clayton from 9 PM to 11 PM by appointment.
          </p>

          <div className="grid grid-cols-2 gap-5 mb-10">
            {AMENITIES.map((a) => (
              <div key={a.label} className="flex items-center gap-3 border border-white/10 px-4 py-4">
                <a.icon className="w-5 h-5 text-[#d4af37]" strokeWidth={1.5} />
                <span className="font-body text-sm text-white/70">{a.label}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 text-white/50 text-sm mb-8">
            <ShieldCheck className="w-5 h-5 text-[#d4af37]" strokeWidth={1.5} />
            <span className="font-body">Disinfection between every client · Mobile service available</span>
          </div>

          <Link
            to="/book"
            className="group inline-flex items-center gap-3 bg-[#d4af37] text-black px-10 py-4 overline text-xs hover:bg-[#c5a028] transition-colors duration-300"
            data-testid="visit-book-btn"
          >
            Reserve Your Chair
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function VideoReel() {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);
  const backend = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => {
    api.get("/testimonials").then((r) => setVideos(r.data)).catch(() => {});
  }, []);
  if (videos.length === 0) return null;
  return (
    <section id="testimonials" className="py-32 bg-[#080808] border-y border-white/5 overflow-hidden" data-testid="video-reel-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal>
          <p className="overline text-xs text-[#d4af37] mb-4 flex items-center gap-3"><span className="w-8 h-px bg-[#d4af37]" /> In Their Words</p>
          <h2 className="font-display text-4xl lg:text-6xl text-white mb-4">Client <span className="italic text-[#d4af37]">testimonials</span></h2>
          <p className="text-white/40 font-body text-sm mb-12">Tap any clip to watch with sound.</p>
        </Reveal>
      </div>
      <div className="flex gap-6 overflow-x-auto px-6 lg:px-12 pb-6 snap-x snap-mandatory scrollbar-hide" data-testid="video-reel-track">
        {videos.map((v, i) => (
          <ReelVideo key={v.id} video={v} src={`${backend}${v.url}`} index={i} onOpen={() => setActive({ ...v, src: `${backend}${v.url}` })} />
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-black border border-[#d4af37]/30 p-0 max-w-sm overflow-hidden" data-testid="video-lightbox">
          <DialogTitle className="sr-only">{active?.client_name || "Client testimonial"} video</DialogTitle>
          {active && (
            <div className="relative">
              <video
                src={active.src}
                className="w-full h-auto max-h-[80vh] bg-black"
                controls
                autoPlay
                playsInline
                data-testid="lightbox-video"
              />
              {(active.client_name || active.caption) && (
                <div className="p-4 bg-[#0a0a0a]">
                  <p className="font-body text-white text-sm flex items-center gap-2">
                    {active.client_name || "Verified client"}
                    <BadgeCheck className="w-4 h-4 text-[#d4af37]" />
                  </p>
                  {active.caption && <p className="overline text-[0.55rem] text-[#d4af37] mt-1">{active.caption}</p>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ReelVideo({ video, src, index, onOpen }) {
  const ref = useRef(null);
  const onEnter = () => { if (ref.current) ref.current.play().catch(() => {}); };
  const onLeave = () => { if (ref.current) ref.current.pause(); };
  return (
    <Reveal delay={index * 0.06}>
      <button
        type="button"
        className="relative w-[260px] sm:w-[300px] aspect-[9/16] shrink-0 snap-center border border-white/10 overflow-hidden group bg-black block text-left"
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onClick={onOpen}
        data-testid={`reel-video-${index}`}
      >
        <video
          ref={ref}
          src={src}
          className="w-full h-full object-cover pointer-events-none"
          muted
          loop
          playsInline
          preload="metadata"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/55 backdrop-blur-sm border border-[#d4af37]/40 px-2.5 py-1 pointer-events-none">
          <BadgeCheck className="w-3 h-3 text-[#d4af37]" />
          <span className="overline text-[0.5rem] text-[#d4af37]">Verified client</span>
        </span>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm border border-white/30 flex items-center justify-center opacity-100 group-hover:scale-110 transition-transform duration-300 pointer-events-none">
          <Play className="w-4 h-4 text-white fill-white ml-0.5" />
        </div>
        {(video.client_name || video.caption) && (
          <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-none">
            {video.client_name && <p className="font-body text-white text-sm font-semibold">{video.client_name}</p>}
            {video.caption && <p className="overline text-[0.55rem] text-[#d4af37] mt-1">{video.caption}</p>}
          </div>
        )}
      </button>
    </Reveal>
  );
}

function SocialSection() {
  const [photos, setPhotos] = useState([]);
  const backend = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => {
    api.get("/gallery").then((r) => setPhotos(r.data.slice(0, 6))).catch(() => {});
  }, []);
  const grid = [
    ...photos.map((g) => `${backend}${g.url}`),
    ...IMG.work,
  ].slice(0, 6);

  return (
    <section id="follow" className="py-32 px-6 lg:px-12 max-w-[1400px] mx-auto" data-testid="social-section">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 mb-14">
          <div className="flex items-center gap-5">
            <img src="/work/reek.webp" alt="Reek" className="w-16 h-16 rounded-full object-cover border-2 border-[#d4af37]" onError={(e) => { e.target.style.display = "none"; }} />
            <div>
              <p className="font-display text-2xl text-white leading-none">{SOCIALS.instagram.handle}</p>
              <p className="text-white/40 font-body text-sm mt-1">Follow the journey — fresh cuts daily.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href={SOCIALS.instagram.url} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 bg-[#d4af37] text-black px-6 py-3 overline text-xs hover:bg-[#c5a028] transition-colors" data-testid="follow-instagram-btn">
              <Instagram className="w-4 h-4" strokeWidth={2} /> Follow on Instagram
            </a>
          </div>
        </div>
      </Reveal>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {grid.map((src, i) => (
          <Reveal key={i} delay={(i % 3) * 0.06}>
            <a href={SOCIALS.instagram.url} target="_blank" rel="noopener noreferrer" className="relative block aspect-square overflow-hidden group border border-white/5" data-testid={`social-photo-${i}`}>
              <img src={src} alt="Cut by Reek" loading="lazy" className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-300 flex items-center justify-center">
                <Instagram className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" strokeWidth={1.5} />
              </div>
            </a>
          </Reveal>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-4 mt-12">
        <SocialPill icon={Instagram} label={SOCIALS.instagram.handle} url={SOCIALS.instagram.url} testid="social-ig" />
        <SocialPill icon={TikTokIcon} label={SOCIALS.tiktok.handle} url={SOCIALS.tiktok.url} testid="social-tiktok" />
        <SocialPill icon={Facebook} label={SOCIALS.facebook.handle} url={SOCIALS.facebook.url} testid="social-fb" />
      </div>
    </section>
  );
}

function SocialPill({ icon: Icon, label, url, testid }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2.5 border border-white/15 px-5 py-3 overline text-[0.65rem] text-white/70 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors" data-testid={testid}>
      <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
    </a>
  );
}

export default function Home() {
  const services = useServices();
  return (
    <div className="bg-[#050505]" data-testid="home-page">
      <Hero />
      <MarqueeStrip />
      <Manifesto />
      <MeetReek />
      <Services services={services} />
      <WhatsIncluded />
      <Memberships />
      <Gallery />
      <Reviews services={services} />
      <VideoReel />
      <SocialSection />
      <Visit />
    </div>
  );
}
